import * as React from 'react';
import {DragDropContext, Draggable, DragUpdate, Droppable} from 'react-beautiful-dnd';
import * as classNames from "classnames";
import {arrayMove} from "../../../utils/ArrayUtils";

interface ComponentProps<T> {
	/**
	 * Items to display.
	 */
	items: T[];
	/**
	 * Order change callback.
	 * @param {T[]} items
	 */
	onOrderChange: (items: T[]) => void;
	/**
	 * Header renderer
	 * @returns {JSX.Element}
	 */
	renderHeaderBody?: () => JSX.Element;
	/**
	 * Row body renderer
	 * @param {T} item
	 * @returns {JSX.Element}
	 */
	renderRowBody?: (item: T, index: number) => JSX.Element;
	/**
	 * Flag if drop is disabled
	 */
	isDropDisabled?: boolean;
	/**
	 * Row click callback
	 */
	onRowClick?: (item: T) => void;
}

type Props<T> = ComponentProps<T>;

export default abstract class SortableItemsTable<T> extends React.PureComponent<Props<T>> {

	render() {
		return (
			<DragDropContext onDragEnd={this.onDragEnd}>
				<Droppable droppableId="droppable" isDropDisabled={this.props.isDropDisabled}>
					{provided => (
						<div ref={provided.innerRef}>
							<table style={{tableLayout: "auto"}} className="l-table-default table-hover">
								<thead>
								<tr>
									{this.props.renderHeaderBody ? this.props.renderHeaderBody() : this.renderHeaderBody()}
								</tr>
								</thead>
								<tbody style={{tableLayout: "auto"}}>
								{this.props.items.map((item, index) => (
									<Draggable key={this.itemToId(item)} draggableId={this.itemToId(item)} index={index}>
										{(provided, snapshot) => (
											<tr ref={provided.innerRef}
												{...provided.draggableProps}
												{...provided.dragHandleProps}
												className={classNames({
													"c-list-item": true,
													"isDragging": Boolean(snapshot.isDragging)
												})}
												style={{...provided.draggableProps.style}}
												onClick={() => this.props.onRowClick && this.props.onRowClick(item)}>
												{this.props.renderRowBody ?
													this.props.renderRowBody(item, index)
													: this.renderRowBody(item)}
											</tr>
										)}
									</Draggable>
								))}
								</tbody>
							</table>
						</div>
					)}
				</Droppable>
			</DragDropContext>
		)
	}

	abstract renderHeaderBody(): JSX.Element

	abstract renderRowBody(item: T): JSX.Element

	abstract itemToId(item: T): string

	/**
	 * Call back on stop dragging action.
	 * @param result
	 */
	onDragEnd = (result: DragUpdate) => {
		// dropped outside the list
		if (!result.destination) {
			return;
		}
		this.props.onOrderChange(arrayMove(this.props.items, result.source.index, result.destination.index, false));
	};
}
